import React, {
  useCallback,
  useContext,
  useState,
  useMemo,
  useRef,
  useEffect,
  useLayoutEffect
} from "react";
import ActivityService from "../../../../services/ActivityService";
import { TranslationService } from "../../../../services/TranslationService";
import { EmailCompose } from "../../components/EmailCompose";
import { deleteActivityAction, DisplayFileIcon } from "../ActivityHistory";
import {
  ActivityListResponse,
  ActivityOrigin
} from "../entities/AcitivityListResponse";
import { EmailEdit } from "./EmailEdit";
import { ActivityHistoryType } from "../../../shared/enums";
import { ToastService } from "../../../shared/bootstrap/Toast";
import { addIf } from "../../../../utils/Utils";
import { ActivityPendingResponse } from "../entities/AcitivityPendingResponse";
import CompanyService from "../../../../services/CompanyService";
import { AIAgentType } from "../../../../entities/company/AIBilling/AIBilling";
import { extractBodyContent } from "../../../../utils/FuncUtils";
import { EmailAICopilotPanelWithLoading } from "../../../shared/components/EmailAICopilotPanel";
import CounterMsgContext from "../../../shared/CounterMsgContext";
import { useWinStore } from "../../../../components/windows/windowStore";
import FloatingPanelService from "../../../shared/FloatingPanel";
import { SafeHtml } from "../../../../components/SafeHtml";
import { usePinActivityHook } from "../hooks/pinActivityHook";
import ClientDetailContext from "../../ClientDetailContext";
import {
  buildReplyAllRecipients,
  buildReplyRecipients
} from "../../../../utils/emailReplyAll";
import { ActivityItemShell } from "../activityManager/shell/ActivityItemShell";
import ModalService from "../../../shared/bootstrap/modal/Modal";
import TooltipComponent from "../../../shared/TooltipComponent";
import { LabelActionText } from "./NewComment";
import {
  formatDateDigits,
  formatDateTimeDigits
} from "../../../../utils/FormatUtils";
import { restTimezone } from "../../../../utils/ParseUtils";
import iconEmail from "../../../../assets/img/iconEmail.svg";
import ActionService from "../../../../services/ActionService";
import Loading from "../../../shared/components/Loading";
import { renderToStaticMarkup } from "react-dom/server";
import { SanitizeUtils } from "../../../../utils/SanitizeUtils";

// El nombre de la Accion Automatica no viaja en /activity/list (el join
// contra Action esta apagado por costo). Se resuelve on-demand, solo
// cuando el usuario abre el tooltip, para no pegarle a /action/get por
// cada fila renderizada. Cache a nivel modulo para no repetir el fetch
// si la misma accion aparece en varias actividades.
const automaticActionTooltipCache = new Map<number, Promise<string>>();

const getAutomaticActionTooltipHtml = (
  actionId: number,
  translate: typeof TranslationService.translate
) => {
  let cached = automaticActionTooltipCache.get(actionId);
  if (!cached) {
    cached = ActionService.get(actionId).then((result) => {
      const name = result instanceof Error ? undefined : result.item?.Name;
      if (!name) {
        return "";
      }
      return renderToStaticMarkup(
        <div>
          <div className="small text-uppercase mb-1">
            {translate.AutomaticProcess}
          </div>
          <strong className="d-block mb-1">{name}</strong>
          <div className="small">{translate.AutomaticProcessDescription}</div>
        </div>
      );
    });
    automaticActionTooltipCache.set(actionId, cached);
  }
  return cached;
};

const formatEmailSender = (fromName?: string, fromEmail?: string) => {
  if (fromName && fromName.trim()) {
    return `${fromName.trim()} <${fromEmail || ""}>`;
  }
  if (fromEmail) {
    return fromEmail.split("@")[0];
  }
  return "";
};

const EmailRecipients = ({
  to,
  cc,
  bcc,
  translate
}: {
  to?: string;
  cc?: string;
  bcc?: string;
  translate: any;
}) => {
  const recipientsRef = React.useRef<HTMLDivElement>(null);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [tooltipText, setTooltipText] = useState("");

  const recipients: string[] = [];
  if (to) recipients.push(to);
  if (cc) recipients.push(cc);
  if (bcc) recipients.push(bcc);

  const recipientsText = recipients.join(", ");
  const fullText =
    recipients.length > 0 ? `${translate.To} ${recipientsText}` : "";

  useEffect(() => {
    setTooltipText(fullText);
  }, [fullText]);

  useEffect(() => {
    const checkOverflow = () => {
      if (recipientsRef.current) {
        const isOverflowing =
          recipientsRef.current.scrollWidth > recipientsRef.current.clientWidth;
        setHasOverflow(isOverflowing);
      }
    };
    const currentElement = recipientsRef.current;
    checkOverflow();
    let resizeObserver: ResizeObserver | null = null;
    if (currentElement && typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => {
        checkOverflow();
      });
      resizeObserver.observe(currentElement);
    }
    window.addEventListener("resize", checkOverflow);
    const timeoutId = setTimeout(checkOverflow, 0);
    return () => {
      if (resizeObserver && currentElement) {
        resizeObserver.unobserve(currentElement);
      }
      window.removeEventListener("resize", checkOverflow);
      clearTimeout(timeoutId);
    };
  }, [to, cc, bcc, fullText]);

  useLayoutEffect(() => {
    if (recipientsRef.current) {
      const isOverflowing =
        recipientsRef.current.scrollWidth > recipientsRef.current.clientWidth;
      setHasOverflow(isOverflowing);
    }
  }, [fullText]);

  if (recipients.length === 0) {
    return null;
  }

  return (
    <TooltipComponent title={hasOverflow ? tooltipText : false}>
      <div
        ref={recipientsRef}
        style={{
          maxWidth: "700px",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          display: "inline-block",
          fontWeight: "normal"
        }}
      >
        {fullText}
        {hasOverflow && <span className="ms-1">▼</span>}
      </div>
    </TooltipComponent>
  );
};

const getActivityTypeName = (
  data:
    | { ActivityType?: { ActivityTypeName: string }; ActivityTypeID?: number }
    | object
): string | null => {
  if (
    "ActivityType" in data &&
    data.ActivityType?.ActivityTypeName !== undefined
  ) {
    return data.ActivityType.ActivityTypeName;
  }
  if ("ActivityTypeID" in data && data.ActivityTypeID) {
    const activityType = CompanyService.getActivityTypes().find(
      (x) => x.ActivityTypeID === data.ActivityTypeID
    );
    return activityType?.ActivityTypeName || null;
  }
  return null;
};

const DisplayGroupName = (
  data: { groupName?: string; Group?: { Name: string } } | object
) => {
  if (
    "groupName" in data &&
    data.groupName &&
    (data.groupName as string).length > 0
  ) {
    return (
      <div style={{ marginRight: 10 }}>
        <strong>{CompanyService.getGroupName()}</strong>:{" "}
        <em>{data.groupName as string}</em>
      </div>
    );
  }
  if ("Group" in data && (data as { Group?: { Name: string } }).Group?.Name) {
    return (
      <div style={{ marginRight: 10 }}>
        <strong>{CompanyService.getGroupName()}</strong>:{" "}
        <em>{(data as { Group: { Name: string } }).Group.Name}</em>
      </div>
    );
  }
  return <></>;
};

const DisplayTagIcon = (
  data: { tagList: string } | { tags: string } | object,
  list: boolean
) => {
  let tags = "";
  if ("tagList" in data) tags = data.tagList;
  else if ("tags" in data) tags = data.tags;
  if (!tags || tags.length === 0) return <></>;
  return (
    <div className="d-flex align-items-center">
      <TooltipComponent title={TranslationService.translate.Tags}>
        <i className="fa fa-tag activity-icon-color my-2" />
      </TooltipComponent>
      {list && (
        <div className="d-flex align-items-center gap-2">
          {tags.split(",").map((tag, i) => (
            <div key={i}>
              <LabelActionText bgColor={false} text={tag} action={() => {}} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const AI_STRIP_STYLE: React.CSSProperties = {
  backgroundColor: "#6366f1",
  color: "white",
  fontWeight: 700,
  fontSize: 11,
  textTransform: "uppercase",
  margin: "-8px -8px 8px -8px",
  padding: "4px 8px",
  borderRadius: "6px 6px 0 0"
};

export const AIAgentStrip = ({ text }: { text: string }) => (
  <div style={AI_STRIP_STYLE}>✨ {text}</div>
);

export const replyAtt = "1";
export const forwardAtt = "-1";

export const ActivityEmail = ({
  data,
  reload,
  reloadClientDetail
}: {
  data: ActivityListResponse.Item;
  reload: () => void;
  reloadClientDetail: () => void;
}) => {
  const { translate } = TranslationService;
  const { getCounterInbox } = useContext(CounterMsgContext);
  const { open } = useWinStore.getState();
  const actionsRef = useRef<any[]>([]);
  // Permite que Responder / Responder a todos / Reenviar arrastren las facturas
  // seleccionadas en el listado del detalle de cliente, igual que el nuevo email.
  const clientDetailContextValues = useContext(ClientDetailContext);

  const [isOverflowing, setIsOverflowing] = useState(false);
  const wrapperRef = useRef(null);
  const maxHeight = 200;

  const getMailField = (primary: string, fallback: string) =>
    (data?.[primary as keyof ActivityListResponse.Item] as
      | string
      | undefined) ??
    (data?.[fallback as keyof ActivityListResponse.Item] as string | undefined);
  const {
    icon: pinIcon,
    canPin,
    togglePinActivity,
    tooltip
  } = usePinActivityHook({ data, reload });

  const openEmailModal = async (
    subject: string,
    to?: string,
    groupId?: string,
    groupName?: string,
    replyAtt?: string,
    CC?: string,
    BCC?: string
  ) => {
    try {
      await ActivityService.setRead({ ids: [data.ID], read: "1" });

      const copilotModalIdentifier = `copilot-${data.ID}`;
      const replyModalIdentifier = `reply-${data.ID}`;

      // Cerrar ventana de respuesta previa si existe
      const {
        open: openWin,
        windows: currentWindows,
        close: closeWin
      } = useWinStore.getState();
      const existingReply = currentWindows.find(
        (w) => w.identifier === replyModalIdentifier
      );
      if (existingReply) closeWin(existingReply.id);

      const onSubmit = () => {
        reload();
        reloadClientDetail && reloadClientDetail();
        const { windows: ws, close: closeAll } = useWinStore.getState();
        [copilotModalIdentifier, replyModalIdentifier].forEach((ident) => {
          const win = ws.find((w) => w.identifier === ident);
          if (win) closeAll(win.id);
        });
      };

      openWin({
        title: subject || translate.EmailToClient,
        content: (
          <EmailCompose
            personId={data.PersonId.toString()}
            defaultSubject={subject}
            defaultTo={to}
            defaultCC={CC}
            defaultBCC={BCC}
            defaultGroupId={groupId}
            defaultGroupLabel={groupName}
            prevMessageId={data.ID.toString()}
            clientDetailContext={clientDetailContextValues}
            onSubmit={onSubmit}
            reply={replyAtt}
            files={data.files}
            isReplyFoward
          />
        ),
        width: 900,
        height: 669,
        identifier: replyModalIdentifier
      });
    } catch (error) {
    } finally {
      await getCounterInbox();
    }
  };

  const openEmailWithAssistant = useCallback(async () => {
    try {
      FloatingPanelService.hidePanel();

      const vw = typeof window !== "undefined" ? window.innerWidth : 1280;
      const vh = typeof window !== "undefined" ? window.innerHeight : 800;
      const combinedWidth = Math.min(vw - 40, 1240);
      const combinedHeight = vh - 60;
      const x = Math.max(16, (vw - combinedWidth) / 2);
      const y = Math.max(16, (vh - combinedHeight) / 2);

      open({
        title: data.Subject || translate.EmailToClient,
        content: (
          <EmailAICopilotPanelWithLoading
            emailId={data.ID}
            personId={data.PersonId}
            actions={actionsRef.current}
            reload={reload}
            onCopilotComplete={() => {
              reloadClientDetail?.();
              reload();
              getCounterInbox();
            }}
          />
        ),
        width: combinedWidth,
        height: combinedHeight,
        x,
        y,
        noHeader: true,
        identifier: `copilot-${data.ID}`
      });
    } catch (error) {
      ToastService.showToast(
        translate.ErrorProcessingRequest,
        undefined,
        "danger"
      );
    } finally {
      await getCounterInbox();
    }
  }, [
    data.ID,
    data.Subject,
    data.PersonId,
    open,
    reload,
    getCounterInbox,
    translate.EmailToClient,
    translate.ErrorProcessingRequest,
    reloadClientDetail
  ]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const downloadIo = () => {
    ActivityService.exportMessageIo(data.ID, data.IOCount);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const reply = () => {
    const recipients = buildReplyRecipients({
      from: getMailField("From", "from"),
      currentUserEmail: CompanyService.getCurrentUserEmails()
    });

    openEmailModal(
      "RE: " + data.Subject,
      recipients.to,
      data.groupId,
      data.groupName,
      replyAtt
    );
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const replyAll = () => {
    const recipients = buildReplyAllRecipients({
      from: getMailField("From", "from"),
      to: getMailField("To", "to"),
      cc: getMailField("cc", "CC"),
      bcc: getMailField("bcc", "BCC"),
      currentUserEmail: CompanyService.getCurrentUserEmails()
    });

    openEmailModal(
      "RE: " + data.Subject,
      recipients.to,
      data.groupId,
      data.groupName,
      replyAtt,
      recipients.cc,
      recipients.bcc
    );
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const forward = () => {
    openEmailModal(
      "FW: " + data.Subject,
      undefined,
      data.groupId,
      data.groupName,
      forwardAtt
    );
  };
  const edit = useCallback(async () => {
    try {
      const emailRequest = await ActivityService.get(data.ID);
      await ActivityService.setRead({ ids: [data.ID], read: "1" });
      if (emailRequest instanceof Error) {
        ToastService.showToast(
          translate.ErrorProcessingRequest,
          undefined,
          "danger"
        );
        return;
      }

      // Cerrar el modal anterior si existe
      FloatingPanelService.hidePanel();

      FloatingPanelService.showPanel({
        title: translate.EmailToClient,
        children: <EmailEdit email={emailRequest.item} onSubmit={reload} />,
        width: 600,
        height: 500
      });
    } catch (error) {
    } finally {
      await getCounterInbox();
    }
  }, [
    data.ID,
    getCounterInbox,
    reload,
    translate.EmailToClient,
    translate.ErrorProcessingRequest
  ]);

  const replyAction = useCallback(
    () => ({
      icon: "fa-reply",
      onClick: reply,
      tooltip: TranslationService.translate.Reply
    }),
    [reply]
  );

  const replyAllAction = useCallback(
    () => ({
      icon: "fa-reply-all",
      onClick: replyAll,
      tooltip: TranslationService.translate.ReplyAll
    }),
    [replyAll]
  );

  const forwardAction = useCallback(
    () => ({
      icon: "fa-arrow-alt-right",
      onClick: forward,
      tooltip: TranslationService.translate.Foward
    }),
    [forward]
  );

  const sparkleAction = useCallback(
    () => ({
      icon: "emoji:✨",
      onClick: openEmailWithAssistant
    }),
    [openEmailWithAssistant]
  );

  const actions = useMemo(() => {
    const actionsArray = [
      ...addIf(canPin, {
        icon: pinIcon,
        onClick: togglePinActivity,
        tooltip: tooltip
      }),
      ...addIf(
        CompanyService.canActOnMessage() &&
          CompanyService.canShowAgentAI(AIAgentType.Categorize) &&
          (data.Inbound || CompanyService.isBackOfficeUser()),
        sparkleAction()
      ),
      ...addIf(data.hasIOs, {
        icon: "fa-download",
        onClick: downloadIo,
        tooltip: TranslationService.translate.DownloadAttachedInvoiceList
      }),
      ...(CompanyService.canActOnMessage()
        ? [replyAction(), replyAllAction(), forwardAction()]
        : []),
      ...addIf(CompanyService.canDo("editmail"), {
        icon: "fa-pen",
        onClick: edit,
        tooltip: TranslationService.translate.Edit
      }),
      ...addIf(
        CompanyService.canDo("deletemail") &&
          (CompanyService.getIsAdmin() ||
            data.UserID === Number(CompanyService.getCompanyToken()?.userid)),
        deleteActivityAction(
          data.ID,
          translate.EmailDeleteTitle,
          translate.EmailDeleteMessage,
          reload
        )
      )
    ];
    actionsRef.current = actionsArray;
    return actionsArray;
  }, [
    data.ID,
    data.hasIOs,
    data.Inbound,
    data.UserID,
    reload,
    translate.EmailDeleteTitle,
    translate.EmailDeleteMessage,
    sparkleAction,
    replyAction,
    replyAllAction,
    forwardAction,
    edit,
    downloadIo,
    pinIcon,
    canPin,
    togglePinActivity,
    tooltip
  ]);

  const onReadMoreClicked = async (id: number) => {
    try {
      const emailRequest = await ActivityService.get(data.ID);
      if (emailRequest instanceof Error) {
        ToastService.showToast(
          TranslationService.translate.ErrorProcessingRequest,
          undefined,
          "danger"
        );
        return;
      }
      await ActivityService.setRead({ ids: [data.ID], read: "1" });

      // Cerrar el modal anterior si existe
      FloatingPanelService.hidePanel();

      FloatingPanelService.showPanel({
        title: translate.Email,
        children: <EmailEdit email={emailRequest.item} actions={actions} />,
        width: 700,
        height: 600
      });
    } catch (error) {
    } finally {
      await getCounterInbox();
    }
  };

  useEffect(() => {
    const element = wrapperRef.current;
    if (!element) return;

    const resizeObserver = new ResizeObserver(() => {
      if (element.scrollHeight > maxHeight) {
        setIsOverflowing(true);
      } else {
        setIsOverflowing(false);
      }
    });

    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, [data.Content]);

  const getMessageDebounce = async () => {
    try {
      const response = await ActivityService.getMessageContentBounce(
        data.ID.toString()
      );
      if (response instanceof Error) {
        throw new Error("");
      } else {
        ModalService.showDefaultModal({
          title: translate.Error,
          message: () => (
            <SafeHtml html={response.body} style={{ overflow: "auto" }} />
          ),
          size: "modal-lg"
        });
      }
    } catch {
      ModalService.showDefaultModal({
        title: translate.Error,
        message: translate.ErrorProcessingRequest
      });
    }
  };

  const isEmailError = data.Failed;
  const hasBounceData = isEmailError && data.HasBounceData;

  const iconNode = (
    <TooltipComponent
      title={
        isEmailError
          ? TranslationService.translate.EmailError
          : TranslationService.translate.EmailOk
      }
    >
      {isEmailError ? (
        <i
          className="fas fa-exclamation-circle text-danger"
          onClick={hasBounceData ? () => getMessageDebounce() : () => {}}
        />
      ) : (
        <img className="img-fluid" src={iconEmail} alt="icon email" />
      )}
    </TooltipComponent>
  );

  const senderFormatted = formatEmailSender(data.FromName, data.From);
  const headerNode = (
    <div className="type">
      {data.Subject && (
        <div>
          <strong>{data.Subject}</strong>
        </div>
      )}
      {senderFormatted && <div className="creator">{senderFormatted}</div>}
      <div>
        <EmailRecipients
          to={data.To}
          cc={data.cc}
          bcc={data.bcc}
          translate={translate}
        />
      </div>
    </div>
  );

  const activityTypeName = getActivityTypeName(data);
  const isAutomaticAction = data.ActionID != null;
  const userText = isAutomaticAction
    ? translate.Automatic
    : data.CreatorUsername || data.Username || "";

  const automaticActionTooltipClass = `automatic-action-tooltip-${data.ID}`;

  const loadAutomaticActionTooltip = () => {
    // Si el backend algun dia manda ActionName denormalizado, no hace
    // falta pedir nada: el contenido ya se renderizo resuelto mas abajo.
    if (data.ActionName) {
      return;
    }
    const actionId = data.ActionID;
    if (actionId == null) {
      return;
    }
    const tooltipElement = document.getElementsByClassName(
      automaticActionTooltipClass
    )[0];
    if (!tooltipElement) {
      return;
    }
    getAutomaticActionTooltipHtml(actionId, translate).then((html) => {
      tooltipElement.outerHTML = html || "<div></div>";
    });
  };

  const automaticActionTooltip = !isAutomaticAction ? (
    false
  ) : data.ActionName ? (
    <div>
      <div className="small text-uppercase mb-1">
        {translate.AutomaticProcess}
      </div>
      <strong className="d-block mb-1">{data.ActionName}</strong>
      <div className="small">{translate.AutomaticProcessDescription}</div>
    </div>
  ) : (
    <div className={automaticActionTooltipClass}>
      <Loading />
    </div>
  );

  const dateNode = (
    <div className="dateTertiaryQ d-flex align-items-center gap-2">
      {activityTypeName && (
        <span className="badge bg-secondary">{activityTypeName}</span>
      )}
      {userText && (
        <TooltipComponent
          title={automaticActionTooltip}
          onInserted={loadAutomaticActionTooltip}
        >
          <small className="text-secondary">{userText}</small>
        </TooltipComponent>
      )}
      {(() => {
        const date = new Date(data.date);
        return date.getHours() === 0 && date.getMinutes() === 0
          ? formatDateDigits(date)
          : formatDateTimeDigits(restTimezone(date));
      })()}
    </div>
  );

  const contentNode = (
    <>
      <div style={{ maxWidth: "100%", overflow: "hidden" }}>
        <div
          ref={wrapperRef}
          className="activity-email-content-wrapper"
          style={{
            maxHeight: `${maxHeight}px`,
            overflow: "hidden",
            maxWidth: "100%",
            wordBreak: "break-word",
            overflowWrap: "break-word"
          }}
        >
          <SafeHtml
            html={extractBodyContent(data.Content)}
            className="activity-email-html-content"
            style={{
              maxWidth: "100%",
              overflow: "hidden"
            }}
          />
        </div>
        {(data.HasMoreContent || isOverflowing) && (
          <button
            className="btn btn-intiza ps-0 mt-1 ctaViewCommentsRead"
            onClick={() => onReadMoreClicked(data.ID)}
          >
            {translate.ReadMore}
          </button>
        )}
        {DisplayFileIcon(
          data as unknown as ActivityPendingResponse.Mail,
          ActivityHistoryType.email,
          true
        )}
      </div>
      {DisplayGroupName(data)}
      {DisplayTagIcon(data, true)}
    </>
  );

  const aiStrip =
    data.Origin === ActivityOrigin.Autonomous ? (
      <AIAgentStrip text={translate.RepliedByAIAgent} />
    ) : undefined;

  const compactLine = (
    <>
      <strong>{data.Subject ?? ""}</strong>
      {" - "}
      {SanitizeUtils.htmlToPlainText(extractBodyContent(data.Content ?? ""))}
    </>
  );

  return (
    <ActivityItemShell
      cardClassName="historyCard"
      iconNode={iconNode}
      headerNode={headerNode}
      dateNode={dateNode}
      content={contentNode}
      actions={CompanyService.canDo("newmail") ? actions : []}
      aiStripNode={aiStrip}
      compactLine={compactLine}
    />
  );
};
